import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  HeartIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  UserIcon,
  TrophyIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline'
import { crmAPI, CustomersAnalytics } from '@/services/api'

export default function CRMAnalytics() {
  const navigate = useNavigate()

  const [analytics, setAnalytics] = useState<CustomersAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true)
        const data = await crmAPI.getCustomersAnalytics()
        setAnalytics(data)
        setError(null)
      } catch (err: any) {
        console.error('Error fetching analytics:', err)
        setError(err.message || 'Erro ao carregar analytics')
      } finally {
        setLoading(false)
      }
    }

    fetchAnalytics()
  }, [])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      customer: 'Cliente',
      supplier: 'Fornecedor',
      both: 'Cliente + Fornecedor',
      prospect: 'Prospect'
    }
    return labels[role] || role
  }

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      customer: 'bg-blue-100 text-blue-800',
      supplier: 'bg-orange-100 text-orange-800',
      both: 'bg-purple-100 text-purple-800',
      prospect: 'bg-gray-100 text-gray-800'
    }
    return colors[role] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando analytics...</p>
        </div>
      </div>
    )
  }

  if (error || !analytics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar Analytics</h3>
            <p className="text-red-600">{error || 'Analytics não disponível'}</p>
            <button
              onClick={() => navigate('/crm/customers')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Voltar para Clientes
            </button>
          </div>
        </div>
      </div>
    )
  }

  const { summary, financial, top_customers } = analytics

  // Calculate percentages for role distribution
  const totalWithRoles = summary.only_customers + summary.only_suppliers + summary.both_roles + summary.prospects
  const customerPercentage = totalWithRoles > 0 ? (summary.only_customers / totalWithRoles) * 100 : 0
  const supplierPercentage = totalWithRoles > 0 ? (summary.only_suppliers / totalWithRoles) * 100 : 0
  const bothPercentage = totalWithRoles > 0 ? (summary.both_roles / totalWithRoles) * 100 : 0
  const prospectPercentage = totalWithRoles > 0 ? (summary.prospects / totalWithRoles) * 100 : 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/crm/customers')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <ChartBarIcon className="w-8 h-8 text-blue-600" />
                Analytics - Métricas CRM
              </h1>
              <p className="text-gray-600 mt-1">Visão analítica completa da base de clientes</p>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">BETA</span>
          </div>
        </div>

        {/* Summary Cards - Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Customers */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total de Clientes</p>
                <p className="text-3xl font-bold mt-2">{summary.total_customers}</p>
                <p className="text-sm text-blue-100 mt-1">
                  {summary.active_customers} ativos
                </p>
              </div>
              <UserGroupIcon className="w-12 h-12 text-blue-200" />
            </div>
          </div>

          {/* Total LTV */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">LTV Total</p>
                <p className="text-2xl font-bold mt-2">{formatCurrency(financial.total_ltv)}</p>
                <p className="text-sm text-green-100 mt-1">
                  Lifetime Value
                </p>
              </div>
              <CurrencyDollarIcon className="w-12 h-12 text-green-200" />
            </div>
          </div>

          {/* Average Health Score */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Health Score Médio</p>
                <p className="text-3xl font-bold mt-2">{Math.round(financial.average_health_score)}</p>
                <div className="mt-2 w-full bg-purple-400 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-white"
                    style={{ width: `${financial.average_health_score}%` }}
                  />
                </div>
              </div>
              <HeartIcon className="w-12 h-12 text-purple-200" />
            </div>
          </div>

          {/* At Risk */}
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Clientes em Risco</p>
                <p className="text-3xl font-bold mt-2">{summary.at_risk}</p>
                <p className="text-sm text-red-100 mt-1">
                  {summary.total_customers > 0 ? ((summary.at_risk / summary.total_customers) * 100).toFixed(1) : 0}% do total
                </p>
              </div>
              <ExclamationTriangleIcon className="w-12 h-12 text-red-200" />
            </div>
          </div>
        </div>

        {/* Role Distribution */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <ChartBarIcon className="w-6 h-6 text-blue-600" />
            Distribuição de Papéis
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Only Customers */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Apenas Cliente</span>
                <UserIcon className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-2xl font-bold text-blue-600">{summary.only_customers}</p>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-blue-600"
                  style={{ width: `${customerPercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{customerPercentage.toFixed(1)}% do total</p>
            </div>

            {/* Only Suppliers */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Apenas Fornecedor</span>
                <BuildingOfficeIcon className="w-5 h-5 text-orange-500" />
              </div>
              <p className="text-2xl font-bold text-orange-600">{summary.only_suppliers}</p>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-orange-600"
                  style={{ width: `${supplierPercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{supplierPercentage.toFixed(1)}% do total</p>
            </div>

            {/* Both Roles */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Cliente + Fornecedor</span>
                <ArrowTrendingUpIcon className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-2xl font-bold text-purple-600">{summary.both_roles}</p>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-purple-600"
                  style={{ width: `${bothPercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{bothPercentage.toFixed(1)}% do total</p>
            </div>

            {/* Prospects */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Prospects</span>
                <UserGroupIcon className="w-5 h-5 text-gray-500" />
              </div>
              <p className="text-2xl font-bold text-gray-600">{summary.prospects}</p>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-gray-600"
                  style={{ width: `${prospectPercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{prospectPercentage.toFixed(1)}% do total</p>
            </div>
          </div>
        </div>

        {/* Top Customers */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <TrophyIcon className="w-6 h-6 text-yellow-500" />
              Top 10 Clientes por LTV
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ranking
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Papel Principal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pedidos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lifetime Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {top_customers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Nenhum cliente encontrado
                    </td>
                  </tr>
                ) : (
                  top_customers.map((customer, index) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/crm/customers/${customer.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {index === 0 && <TrophyIcon className="w-5 h-5 text-yellow-500" />}
                          {index === 1 && <TrophyIcon className="w-5 h-5 text-gray-400" />}
                          {index === 2 && <TrophyIcon className="w-5 h-5 text-orange-600" />}
                          <span className="text-lg font-bold text-gray-700">#{index + 1}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {customer.company ? (
                              <BuildingOfficeIcon className="h-10 w-10 rounded-full bg-blue-100 text-blue-600 p-2" />
                            ) : (
                              <UserIcon className="h-10 w-10 rounded-full bg-gray-100 text-gray-600 p-2" />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                            {customer.company && (
                              <div className="text-sm text-gray-500">{customer.company}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(customer.primary_role)}`}>
                          {getRoleLabel(customer.primary_role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {customer.total_orders}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-green-600">
                          {formatCurrency(customer.lifetime_value)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/crm/customers/${customer.id}`)
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Ver Detalhes
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Resumo Financeiro</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600 font-medium">LTV Médio por Cliente</p>
              <p className="text-2xl font-bold text-blue-600 mt-2">
                {formatCurrency(summary.total_customers > 0 ? financial.total_ltv / summary.total_customers : 0)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600 font-medium">LTV Total da Base</p>
              <p className="text-2xl font-bold text-green-600 mt-2">
                {formatCurrency(financial.total_ltv)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600 font-medium">Saúde Média da Base</p>
              <p className="text-2xl font-bold text-purple-600 mt-2">
                {Math.round(financial.average_health_score)}%
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
